import { gradientGrainStyling } from "$/util/background";
import { useSearchCreations } from "$/util/search";
import { ArrowOutwardRounded, DoubleArrowRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import analytics from "../util/analytics";
import CreationHorizontalList from "./CreationHorizontalList";
import Section from "./Section";

const randomIdeas = [
  "Skull and Roses",
  "Hooded Reaper",
  "Crowned Lion Face",
  "Growling Wolf And Mountains",
  "Wolf with Blue Eyes",
  "Spartan Warrior",
];

function HeroSection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(
    randomIdeas[Math.floor(Math.random() * randomIdeas.length)],
  );

  const { data: topCreations } = useSearchCreations({
    limit: 12,
    orderBy: "rank",
  });

  return (
    <>
      <Box
        py={2}
        pl={1}
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, textAlign: "center" }}
      >
        {topCreations && topCreations.length > 0 && (
          <CreationHorizontalList items={topCreations} disableBlur hideTitle />
        )}
        <Button
          component={Link}
          to="/tattoo-ideas"
          variant="text"
          color="inherit"
          endIcon={<ArrowOutwardRounded />}
        >
          Explore Top Tattoos
        </Button>
      </Box>
      <Box>
        <Section
          id="heroSection"
          bgColor="dark"
          size="medium"
          sx={{
            backgroundColor: "#121212",
            backgroundImage: "url(/images/features/backdrop_woman.webp)",
            backgroundPosition: "calc(60%) bottom",
            backgroundRepeat: "no-repeat",
            backgroundSize: { xs: "350px auto", md: "500px auto" },
          }}
        >
          <Container
            sx={{
              padding: `0 6px`,
              textAlign: "center",
              color: "white",
            }}
          >
            <Grid container alignItems="center" spacing={6}>
              <Grid container item direction="column" xs={12} md={6}>
                <Box textAlign={{ xs: "center", md: "left" }}>
                  <Box component="header" mb={4}>
                    <Typography variant="overline" gutterBottom>
                      AI Tattoo Generator
                    </Typography>
                    <Typography
                      variant="hero"
                      variantMapping={{ hero: "h2" }}
                      gutterBottom
                    >
                      Create unique tattoos in seconds.
                    </Typography>
                  </Box>
                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    spacing={2}
                    justifyContent="flex-start"
                  >
                    <Button
                      onClick={() => {
                        window.scrollTo(0, 0);
                        analytics.track("cta-click", {
                          context: "hero",
                          action: "join",
                          title: "ai_tattoo_generator",
                          button_text: "try_free",
                        });
                      }}
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        px: 4,
                        borderRadius: 2,
                        fontSize: "1.1rem",
                        alignItems: "center",
                        fontWeight: 800,
                        color: "white",
                        ...gradientGrainStyling,
                      }}
                    >
                      Try for free
                      <DoubleArrowRounded sx={{ ml: 1 }} />
                    </Button>
                    {/* <Button
                      component={Link}
                      to="/artists"
                      variant="text"
                      color="inherit"
                      size="large"
                      sx={{
                        px: 2,
                        borderRadius: 2,
                        fontSize: "1.1rem",
                        alignItems: "center",
                        fontWeight: 800,
                        color: "white",
                      }}
                    >
                      For Artists
                      <ArrowOutwardRounded
                        sx={{
                          fontSize: 24,
                          ml: 1,
                        }}
                      />
                    </Button> */}
                  </Stack>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                justifyContent="flex-end"
                display={{
                  xs: "none",
                  md: "flex",
                }}
              >
                <Card
                  sx={{
                    backgroundColor: "rgba(50, 50, 50, 0.8)",
                    borderRadius: 2,
                    boxShadow: 4,
                    maxWidth: 300,
                  }}
                >
                  <CardContent>
                    <TextField
                      fullWidth
                      label="Tattoo Idea"
                      variant="outlined"
                      onChange={(e) => {
                        setPrompt(e.target.value);
                      }}
                      value={prompt}
                      sx={{
                        // all white
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "rgba(170, 170, 170, 0.5)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(170, 170, 170, 0.5)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "rgba(170, 170, 170, 0.5)",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(170, 170, 170, 0.5)",
                        },
                        "& .MuiOutlinedInput-input": {
                          color: "rgba(170, 170, 170, 1)",
                        },
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="info"
                      size="large"
                      fullWidth
                      endIcon={<DoubleArrowRounded />}
                      sx={{
                        mt: 1,
                      }}
                      onClick={() => {
                        window.scrollTo(0, 0);
                        navigate(".", {
                          state: {
                            prompt,
                            autorun: true,
                          },
                        });
                      }}
                    >
                      Create
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Section>
      </Box>
      <Box
        sx={{
          ...gradientGrainStyling,
          color: "white",
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 2, md: 8 }}
          alignItems="center"
          justifyContent={{ xs: "flex-start", md: "center" }}
          py={{ xs: 1, md: 4 }}
          px={4}
          overflow="hidden"
          sx={{
            overflowX: { xs: "scroll", md: "hidden" },
            "& img": {
              filter: "grayscale(100%) invert(1)",
            },
          }}
        >
          <Box minWidth={100}>
            <Typography
              variant="overline"
              sx={{ mt: 2, mb: 1, color: "inherit", display: "block" }}
            >
              As seen on{" "}
            </Typography>
          </Box>
          <img src="/images/inked-logo.png" alt="Inked Magazine" height="30" />
          <img src="/images/huffpost-logo.svg" alt="Huffington Post" height="20" />
          <img src="/images/yahoo-news-logo.svg" alt="Yahoo News" height="20" />
          <img
            src="/images/the-information-logo-solid.svg"
            alt="The Information"
            height="30"
          />
        </Stack>
      </Box>
    </>
  );
}

export default HeroSection;
